import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getFile, updateFile } from "../redux/actions/fileActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { textFieldStyles } from "../styles/textFieldStyles";

import { fetchRelationships } from "../redux/actions/relationshipActions";
import {
  Container,
  Tabs,
  Autocomplete,
  Tab,
  Box,
  TextField,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { verticalTabStyles } from "../styles/verticalTabStyles";
import SubmitButton from "../components/SubmitButton";
import CancelButton from "../components/CancelButton";
// import File from "../../../server/models/File";

const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};

const inputStyles = {
  width: "30%",
  marginBottom: 2,
};

const CreateFilePage = () => {
  const { t } = useTranslation(['pages', 'forms', 'buttons', 'common']);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { file } = useSelector((state) => state.fileDetails);

  const relationships = useSelector(
    (state) => state.relationshipList.relationships
  );
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const [userRole, setUserRole] = useState(null);
  const breadcrumbs = [
    { label: t('navigation:home'), href: "/" },
    { label: t('navigation:files'), href: "/files" },
    { label: `${file?.name}`, href: `/files/${id}` },
  ];
  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedParties, setSelectedParties] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [reference, setReference] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createdOn, setCreatedOn] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [language, setLanguage] = useState("English");
  const [categories, setCategories] = useState("");
  const [officeMembers, setOfficeMembers] = useState([
    { role: "", member: "" },
  ]);
  const [opposingPartyFileNumber, setOpposingPartyFileNumber] = useState("");

  const [showDateOfAccident, setShowDateOfAccident] = useState(false); // State for showing the field
  const [dateOfAccident, setDateOfAccident] = useState(""); // State for the Date of Accident value
  
  // Add new state variables for Liability Law fields
  const [clientLicencePlate, setClientLicencePlate] = useState("");
  const [otherPartyLicencePlate, setOtherPartyLicencePlate] = useState("");
  const [insurancePolicyNumber, setInsurancePolicyNumber] = useState("");

  // Type options with English database values and translated labels
  const typeOptions = [
    { value: "Liability Law", label: t('pages:files.types.liability_law') },
    { value: "Civil Law", label: t('pages:files.types.civil_law') },
    { value: "Labor Law", label: t('pages:files.types.labor_law') },
    { value: "Administrative Law", label: t('pages:files.types.administrative_law') },
    { value: "Tax Law", label: t('pages:files.types.tax_law') },
    { value: "Construction Law", label: t('pages:files.types.construction_law') },
    { value: "Rental & Residential Law", label: t('pages:files.types.rental_residential_law') },
    { value: "Environment & Spatial Planning Law", label: t('pages:files.types.environment_spatial_law') },
    { value: "Corporate Law", label: t('pages:files.types.corporate_law') },
    { value: "Military Law", label: t('pages:files.types.military_law') },
    { value: "Education Law", label: t('pages:files.types.education_law') },
    { value: "Other", label: t('pages:files.types.other') },
    { value: "Personal & Family Law", label: t('pages:files.types.personal_family_law') },
    { value: "Legal Entity Law", label: t('pages:files.types.legal_entity_law') },
    { value: "Social Security Law", label: t('pages:files.types.social_security_law') },
    { value: "Criminal Law", label: t('pages:files.types.criminal_law') },
    { value: "Disciplinary Law", label: t('pages:files.types.disciplinary_law') },
    { value: "Law of Obligations", label: t('pages:files.types.law_obligations') },
    { value: "Insurance Law", label: t('pages:files.types.insurance_law') },
    { value: "Immigration Law", label: t('pages:files.types.immigration_law') },
    { value: "Commercial Law", label: t('pages:files.types.commercial_law') },
  ];

  // Status options with English database values and translated labels
  const statusOptions = [
    { value: "", label: t('forms:options.none') },
    { value: "Active", label: t('common:active') },
    { value: "Inactive", label: t('common:inactive') },
  ];

  const [parties, setParties] = useState([{ type: "", name: "", address: "" }]);

  // Add state variables for rates
  const [rateType, setRateType] = useState("hourly");
  const [hourlyRate, setHourlyRate] = useState(0);
  const [fixedRate, setFixedRate] = useState(0);

  useEffect(() => {
    dispatch(getFile(id));
    dispatch(fetchRelationships());
  }, [dispatch, id]);

  useEffect(() => {
    if (file && file.officeMembers) {
      setName(file.name || "");
      setReference(file.reference || "");
      setType(file.type || "");
      setStatus(file.status || "");
      setOfficeMembers(file.officeMembers);
      setOpposingPartyFileNumber(file.opposingPartyFileNumber || "");
      const initialSelectedMembers = file.officeMembers.map((member) =>
        getSelectedMember(member)
      );
      setSelectedMembers(initialSelectedMembers);

      setShowDateOfAccident(file.type === t('pages:files.types.liability_law')); // Initialize visibility based on file type
      setDateOfAccident(file.dateOfAccident ? file.dateOfAccident.split("T")[0] : ""); // Convert ISO to yyyy-MM-dd
      
      // Initialize Liability Law fields from file
      setClientLicencePlate(file.clientLicencePlate || "");
      setOtherPartyLicencePlate(file.otherPartyLicencePlate || "");
      setInsurancePolicyNumber(file.insurancePolicyNumber || "");
    }
    if (file.parties) {
      setParties(file.parties);
      const initialSelectedParties = file.parties.map((party) =>
        getSelectedParty(party)
      );
      setSelectedParties(initialSelectedParties);
    }
    // Initialize rate values from file
    setRateType(file.rateConfig?.type || "hourly");
    setHourlyRate(file.rateConfig?.hourlyRate || 0);
    setFixedRate(file.rateConfig?.fixedRate || 0);
    
  }, [file, relationships]);

  const getSelectedMember = (member) => {
    console.log("member is ", member.role);
    return (
      relationships?.find((rel) => {
        const isMatch = rel._id === member.member._id;
        //   console.log(`Checking if ${rel._id} === ${member.member._id}: ${isMatch}`);
        return isMatch;
      }) || null
    );
  };

  useEffect(() => {
    console.log("file is ", file);
  }, [file]);

  // Add a new useEffect to handle type changes
  useEffect(() => {
    // Update showDateOfAccident whenever type changes (using English database value)
    setShowDateOfAccident(type === "Liability Law");
  }, [type]);

  const handleSaveChanges = () => {
    const updatedFile = {
      ...file,
      name,
      reference,
      type,
      status,
      officeMembers,
      parties,
      opposingPartyFileNumber,
      rateConfig: {
        type: rateType,
        hourlyRate: rateType === "hourly" ? hourlyRate : 0,
        fixedRate: rateType === "fixed" ? fixedRate : 0,
      },
      dateOfAccident: showDateOfAccident ? dateOfAccident : undefined, // Include Date of Accident only if applicable
      clientLicencePlate: showDateOfAccident ? clientLicencePlate : undefined,
      otherPartyLicencePlate: showDateOfAccident ? otherPartyLicencePlate : undefined,
      insurancePolicyNumber: showDateOfAccident ? insurancePolicyNumber : undefined,
    };
    //   console.log("Saving changes for file:", updatedFile);
    dispatch(updateFile(id, updatedFile));
    // navigate("/files");
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const handleMemberChange = (index, newValue) => {
    const newSelectedMembers = [...selectedMembers];
    newSelectedMembers[index] = newValue;
    setSelectedMembers(newSelectedMembers);

    const newOfficeMembers = [...officeMembers];
    if (newOfficeMembers[index]) {
      newOfficeMembers[index].member = newValue ? newValue._id : "";
    } else {
      // If the element doesn't exist, create it
      newOfficeMembers[index] = { role: "", member: newValue ? newValue._id : "" };
    }
    setOfficeMembers(newOfficeMembers);
  };

  const handleAddOfficeMember = () => {
    setOfficeMembers((prevState) => [...prevState, { role: "", member: "" }]);
    setSelectedMembers((prevState) => [...prevState, null]);
  };

  const getSelectedParty = (party) => {
    // console.log("Finding party:", party);
    const selectedParty =
      relationships?.find((rel) => rel._id === party.name._id) || null;
    // console.log("Selected party:", selectedParty);
    return selectedParty;
  };

  const handlePartyChange = (index, newValue) => {
    const newSelectedParties = [...selectedParties];
    newSelectedParties[index] = newValue;
    setSelectedParties(newSelectedParties);

    const newParties = [...parties];
    newParties[index].name = newValue ? newValue._id : "";
    setParties(newParties);
  };

  const handleAddParty = () => {
    setParties((prevState) => [
      ...prevState,
      { type: "", name: "", address: "" },
    ]);
    setSelectedParties((prevState) => [...prevState, null]);
  };

  const handleDeleteParty = (index) => {
    setParties((prevParties) => {
      const newParties = [...prevParties];
      newParties.splice(index, 1);
      return newParties;
    });

    setSelectedParties((prevSelectedParties) => {
      const newSelectedParties = [...prevSelectedParties];
      newSelectedParties.splice(index, 1);
      return newSelectedParties;
    });
  };

  const handleDeleteOfficeMember = (index) => {
    const newOfficeMembers = [...officeMembers];
    newOfficeMembers.splice(index, 1);
    setOfficeMembers(newOfficeMembers);

    const newSelectedMembers = [...selectedMembers];
    newSelectedMembers.splice(index, 1);
    setSelectedMembers(newSelectedMembers);
  };


  const handleFieldChange = (field, value) => {
    console.log(`Changing ${field} to:`, value);
    
    switch (field) {
      case "name":
        setName(value);
        break;
      case "reference":
        setReference(value);
        break;
      case "type":
        setType(value);
        // Explicitly check and update showDateOfAccident whenever type changes (using English database value)
        const shouldShowFields = value === "Liability Law";
        console.log("Should show Liability Law fields:", shouldShowFields);
        setShowDateOfAccident(shouldShowFields);
        break;
      case "status":
        setStatus(value);
        break;
      case "dateOfAccident":
        setDateOfAccident(value);
        break;
      default:
        break;
    }
  };



  const labelStyles = {
    '& .MuiFormControlLabel-label': {
      fontSize: "10.5px",
      fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
      fontWeight: 400
    }
  };

  return userRole === "Assistant Lawyer" ? (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:files.edit_title')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <Typography variant="h6" component="h1">
          You are not authorized to view this page.
        </Typography>
      </Box>
    </Container>
  ) : (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:files.edit_title')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          backgroundColor: "white",
          padding: "1px 24px 24px 24px ",
          margin: "24px 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "white",
            marginTop: "24px",
            border: "1px solid #d1d1d1",
          }}
        >
          <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1" }}>
            <Box sx={{ marginBottom: "40px", marginTop: "30px" }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                orientation="vertical"
                aria-label="relationship tabs"
                TabIndicatorProps={{
                  sx: {
                    left: 0, // Position the indicator at the start
                  },
                }}
                sx={verticalTabStyles}
              >
                <Tab label={t('pages:files.tabs.general')} />
                <Tab label={t('pages:files.tabs.office_members')} />
                <Tab label={t('pages:files.tabs.parties')} />
                <Tab label={t('pages:files.tabs.rates')} />
              </Tabs>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              paddingLeft: 5,
              paddingTop: 6,
              width: "calc(100% - 200px)",
            }}
          >
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "1rem",
                }}
              >
                <TextField
                  label={t('forms:labels.fileName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  size="small"
                  required
                  sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                />
                
                

                {/* <TextField
                label="Reference"
                variant="standard"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
              /> */}

                <FormControl
                  variant="outlined"
                  size="small"
                  required
                  sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                >
                  <Autocomplete
                    options={statusOptions}
                    getOptionLabel={(option) => option.label || option}
                    value={statusOptions.find(option => option.value === status) || null}
                    onChange={(event, newValue) => {
                      // Store English database value
                      const dbValue = newValue ? newValue.value : "";
                      setStatus(dbValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('forms:labels.fileStatus')}
                        variant="outlined"
                        size="small"
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  required
                  sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                >
                  <Autocomplete
                    options={typeOptions}
                    getOptionLabel={(option) => option.label || option}
                    value={typeOptions.find(option => option.value === type) || null}
                    onChange={(event, newValue) => {
                      // Store English database value
                      const dbValue = newValue ? newValue.value : "";
                      setType(dbValue);
                      // Explicitly handle showing Liability Law fields
                      const showFields = dbValue === "Liability Law";
                      console.log("Setting jurisdiction to:", dbValue);
                      console.log("Should show liability fields:", showFields);
                      setShowDateOfAccident(showFields);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('forms:labels.jurisdiction')}
                        variant="outlined"
                        size="small"
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>
                                  {/* Liability Law fields with proper spacing */}
                  {console.log("Current jurisdiction:", type, "showDateOfAccident:", showDateOfAccident)}
                  {showDateOfAccident && (
                    <>
                      <TextField
                        label={t('pages:files.date_of_accident')}
                        variant="outlined"
                        type="date"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={dateOfAccident}
                        onChange={(e) => setDateOfAccident(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                      <TextField
                        label={t('pages:files.client_licence_plate')}
                        variant="outlined"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={clientLicencePlate}
                        onChange={(e) => setClientLicencePlate(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                      <TextField
                        label={t('pages:files.other_party_licence_plate')}
                        variant="outlined"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={otherPartyLicencePlate}
                        onChange={(e) => setOtherPartyLicencePlate(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                      <TextField
                        label={t('pages:files.insurance_policy_number')}
                        variant="outlined"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={insurancePolicyNumber}
                        onChange={(e) => setInsurancePolicyNumber(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                    </>
                  )}
                  <TextField
                      label={t('pages:files.opposing_party_file_number')}
                      variant="outlined"
                      size="small"
                      sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                      value={opposingPartyFileNumber}
                      onChange={(e) => setOpposingPartyFileNumber(e.target.value)}
                      />

                {/* <TextField
                label="Created On"
                variant="standard"
                type="date"
                value={createdOn}
                onChange={(e) => setCreatedOn(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label={t('pages:files.event_date')}
                variant="standard"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <FormControl variant="standard" sx={{ mr: 3, width: "30%" }}>
                <InputLabel id="language-select-label">{t('forms:labels.language')}</InputLabel>
                <Select
                  labelId="language-select-label"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  label={t('forms:labels.language')}
                >
                  <MenuItem value="English">{t('common:languages.english')}</MenuItem>
                  <MenuItem value="Dutch">{t('common:languages.dutch')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t('forms:labels.categories')}
                variant="standard"
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
              />
              <TextField
                label={t('common:description')}
                variant="standard"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
              /> */}
              </Box>
            )}
            {activeTab === 1 && (
              <Box>
                {officeMembers.map((member, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      required
                      sx={textFieldStyles("30%")}
                    >
                      <InputLabel id={`role-select-label-${index}`}>
                        Role
                      </InputLabel>

                      <Select
                        labelId={`role-select-label-${index}`}
                        value={officeMembers[index].role}
                        onChange={(e) => {
                          const newMembers = [...officeMembers];
                          newMembers[index].role = e.target.value;
                          setOfficeMembers(newMembers);
                        }}
                        label={t('forms:labels.role')}
                        // disabled={
                        //   index === 0 && userRole === "Responsible Lawyer"
                        // } // Disable the first role if user is Responsible Lawyer
                      >
                        <MenuItem value="Responsible Lawyer">
                          {t('pages:files.roles.responsible_lawyer')}
                        </MenuItem>
                        <MenuItem value="Treating Lawyer">
                          {t('pages:files.roles.treating_lawyer')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl
                      variant="standard"
                      sx={{ mr: 3, width: "35%" }}
                    >
                      <CustomAutocomplete
                        options={relationships}
                        value={selectedMembers[index] || null}
                        onChange={(event, newValue) => handleMemberChange(index, newValue)}
                        label={t('forms:labels.member')}
                        showAddNewOption={true}
                        filterType="Member"
                        addNewLabel={t('pages:files.add_new_lawyer')}
                        addNewPath="/relationships/create"
                        index={index}
                        // disabled={
                        //   member.role === "Responsible Lawyer" &&
                        //   userRole !== "Manager"
                        // }
                      />
                    </FormControl>
                    {/* <Autocomplete
                    options={[
                      ...relationships
                        .filter((rel) => rel.type === "Lawyer"),
                      { _id: "add-lawyer", firstName: t('pages:files.add_lawyer') },
                    ]}
                    getOptionLabel={(option) =>
                      option._id === "add-lawyer"
                        ? t('pages:files.add_lawyer')
                        : `${option.uniqueNumber ? option.uniqueNumber + " - "  : ''}${option.firstName} ${
                            option.middleName ? option.middleName + " " : ""
                          }${option.lastName ? option.lastName : ""}`
                    }
                    value={selectedMembers[index] || null}
                    onChange={(event, newValue) => {
                      if (newValue && newValue._id === "add-lawyer") {
                        navigate("/relationships/create");
                      } else {
                        handleMemberChange(index, newValue);
                      }
                    }}
                    sx={{ mr: 3, width: "30%" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('forms:labels.member')}
                        variant="standard"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option._id === "add-lawyer" ? (
                          <AddIcon sx={{ mr: 1 }} />
                        ) : null}
                        {`${option.uniqueNumber ? option.uniqueNumber + " - "  : ''}${option.firstName} ${
                          option.middleName ? option.middleName + " " : ""
                        }${option.lastName ? option.lastName : ""}`}
                      </li>
                    )}
                  /> */}

                    <IconButton
                      color="secondary"
                      onClick={() => handleDeleteOfficeMember(index)}
                      sx={{ marginLeft: 1 }}
                      // disabled={
                      //   index === 0 && userRole === "Responsible Lawyer"
                      // } // Disable the first role if user is Responsible Lawyer
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  color="primary"
                  onClick={handleAddOfficeMember}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('pages:files.add_office_member')}
                </Button>
              </Box>
            )}
            {activeTab === 2 && (
              <Box>
                {parties.map((party, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      required
                      sx={textFieldStyles("30%")}
                    >
                      <InputLabel id={`party-type-select-label-${index}`}>
                        {t('forms:labels.type')}
                      </InputLabel>
                      <Select
                        labelId={`party-type-select-label-${index}`}
                        value={party.type}
                        onChange={(e) => {
                          const newParties = [...parties];
                          newParties[index].type = e.target.value;
                          setParties(newParties);
                        }}
                        label={t('common:type')}
                      >
                        <MenuItem value="Client">{t('pages:files.party_types.client')}</MenuItem>
                        <MenuItem value="Opposite Party">
                          {t('pages:files.party_types.opposite_party')}
                        </MenuItem>
                        <MenuItem value="Paying Party">{t('pages:files.party_types.paying_party')}</MenuItem>
                        <MenuItem value="Advocate Opposite Party">
                          {t('pages:files.party_types.advocate_opposite_party')}
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl variant="standard" sx={textFieldStyles("30%")}>
                      <CustomAutocomplete
                        options={relationships}
                        value={selectedParties[index] || null}
                        onChange={(event, newValue) => handlePartyChange(index, newValue)}
                        label={t('common:name')}
                        filterType="Party"
                        addNewLabel={t('pages:files.add_new_party')}
                        addNewPath="/relationships/create"
                        index={index}
                        showAddNewOption={true}
                      />
                    </FormControl>

                    {/* <TextField
                    label="Address"
                    variant="standard"
                    value={party.address}
                    onChange={(e) => {
                      const newParties = [...parties];
                      newParties[index].address = e.target.value;
                      setParties(newParties);
                    }}
                    margin="normal"
                    size="small"
                    sx={{
                      marginRight: 3,
                      width: "30%",
                      marginTop: "0",
                      marginBottom: "0",
                    }}
                  /> */}

                    <IconButton
                      color="secondary"
                      onClick={() => handleDeleteParty(index)}
                      sx={{ marginLeft: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  color="primary"
                  onClick={handleAddParty}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('pages:files.add_party')}
                </Button>
              </Box>
            )}
            {activeTab === 3 && (
              <Box>
                <FormControl
                  component="fieldset"
                  required
                  sx={{ marginBottom: 2, width: "100%" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      fontSize: "10.5px",
                      fontFamily:
                        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",

                      fontWeight: 400,
                    }}
                  >
                    {t('pages:files.rate_type')}
                  </FormLabel>
                  
                  <RadioGroup
                    row
                    value={rateType}
                    onChange={(e) => setRateType(e.target.value)}
                  >
                    <FormControlLabel
                      value="hourly"
                      control={<Radio />}
                      label={t('pages:files.hourly_rate')}
                      sx={labelStyles}
                    />
                    <FormControlLabel
                      value="fixed"
                      control={<Radio />}
                      label={t('pages:files.fixed_rate')}
                      sx={labelStyles}
                    />
                    <FormControlLabel
                      value="proBono"
                      control={<Radio />}
                      label={t('pages:files.pro_bono')}
                      sx={labelStyles}
                    />
                  </RadioGroup>
                </FormControl>

                {rateType === "hourly" && (
                  <TextField
                    label={t('pages:files.hourly_rate_euro')}
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    sx={textFieldStyles("30%")}
                    size="small"
                  />
                )}

                {rateType === "fixed" && (
                  <TextField
                    label={t('pages:files.fixed_rate_euro')}
                    type="number"
                    value={fixedRate}
                    onChange={(e) => setFixedRate(Number(e.target.value))}
                    sx={textFieldStyles("30%")}
                    size="small"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            // padding: "10px 16px",
            // background: "#fff",
            zIndex: 10,
            backgroundColor: "white",
            color: "#000000",
            padding: "13px 80px",
          }}
        >
          <CancelButton
            variant="outlined"
            onClick={() => navigate(-1)}
            size="small"
          >
            {t('buttons:secondary.cancel')}
          </CancelButton>
          <SubmitButton onClick={handleSaveChanges} size="small">
            {t('pages:files.save_file')}
          </SubmitButton>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateFilePage;
